import { useEffect, useState } from "react";
import { IconShare, IconTick } from "./icons.js";
import { IconButton } from "./ui.js";

/**
 * A link worth pasting into the group chat, as a glyph beside the heading it belongs to.
 *
 * `navigator.share` on a phone opens the share sheet with WhatsApp in it, which is the
 * actual route anything gets to this club. Everywhere else — and on any browser without
 * it — the URL goes to the clipboard instead. A cancelled share sheet is not a failure and
 * must not fall through to a silent copy, so only a real error takes the fallback.
 *
 * Confirmation is the icon turning into a tick for a moment, rather than a line of text
 * appearing. Text next to it would reflow the header every time somebody shared, which is a
 * lot of movement to say "yes, that worked".
 *
 * `path` rather than a whole URL: the origin is this app's, always, and taking one from a
 * caller is how a share button ends up publicising somebody else's domain.
 */
export function ShareLink({
  path,
  label,
  title,
  text,
}: {
  path: string;
  label: string;
  title: string;
  text: string;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const url = `${window.location.origin}${path}`;

  useEffect(() => {
    if (state !== "copied") return;
    const timer = setTimeout(() => setState("idle"), 2000);
    return () => clearTimeout(timer);
  }, [state]);

  async function share() {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        setState("idle");
        return;
      } catch (cause) {
        // They backed out of the sheet. Nothing happened, and nothing should.
        if (cause instanceof Error && cause.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setState("copied");
    } catch {
      // Clipboard access needs a secure context, which a plain-http LAN address is not —
      // and testing on a phone over the office wifi is exactly that. Show the URL so it can
      // still be long-pressed and copied by hand.
      setState("failed");
    }
  }

  return (
    <span className="relative inline-flex">
      <IconButton label={state === "copied" ? "Link copied" : label} onClick={() => void share()}>
        {state === "copied" ? <IconTick /> : <IconShare />}
      </IconButton>

      {/* Floated rather than placed in the flow, so the failure case cannot push a heading
          around on the one screen where it appears. */}
      {state === "failed" ? (
        <span
          className="absolute right-0 top-full z-10 break-all p-3 text-sm"
          style={{
            width: "min(20rem, 70vw)",
            background: "var(--color-surface)",
            color: "var(--color-ink-muted)",
            borderRadius: "var(--radius-control)",
            border: "1px solid var(--color-line-muted)",
          }}
        >
          {url}
        </span>
      ) : null}
    </span>
  );
}
